import { isAxiosError } from "axios";

/** What the user was trying to do — used to build clear fallback messages. */
export type ErrorContext =
  | "sign-in"
  | "session"
  | "user-profile"
  | "video-upload"
  | "signed-url"
  | "s3-upload"
  | "video-history"
  | "dashboard"
  | "audit-trail"
  | "audit-detail"
  | "users"
  | "roles"
  | "permissions"
  | "alerts"
  | "screenshots"
  | "hierarchy"
  | "generic";

const CONTEXT_ACTIONS: Record<ErrorContext, string> = {
  "sign-in": "sign you in",
  session: "start your session",
  "user-profile": "load your profile",
  "video-upload": "upload your video",
  "signed-url": "prepare your video upload",
  "s3-upload": "send your video to storage",
  "video-history": "load upload history",
  dashboard: "load dashboard statistics",
  "audit-trail": "load the audit trail",
  "audit-detail": "load audit details",
  users: "load users",
  roles: "load roles",
  permissions: "load permissions",
  alerts: "load frames",
  screenshots: "load screenshots",
  hierarchy: "load hierarchy data",
  generic: "complete this action",
};

export const CONTEXT_ERROR_TITLES: Record<ErrorContext, string> = {
  "sign-in": "Couldn't sign you in",
  session: "Session expired",
  "user-profile": "Couldn't load your profile",
  "video-upload": "Couldn't upload your video",
  "signed-url": "Couldn't prepare your upload",
  "s3-upload": "Couldn't send your video",
  "video-history": "Couldn't load upload history",
  dashboard: "Couldn't load dashboard",
  "audit-trail": "Couldn't load audit trail",
  "audit-detail": "Couldn't load audit details",
  users: "Couldn't load users",
  roles: "Couldn't load roles",
  permissions: "Couldn't load permissions",
  alerts: "Couldn't load frames",
  screenshots: "Couldn't load screenshots",
  hierarchy: "Couldn't load hierarchy data",
  generic: "Something went wrong",
};

const STATUS_USER_MESSAGES: Record<number, string> = {
  400: "Please check the information you entered and try again.",
  401: "Your session may have expired. Please sign in again.",
  403: "You have insufficient permissions. Contact your administrator if you need access.",
  404: "We couldn't find what you're looking for.",
  408: "The request took too long. Please try again.",
  429: "Too many requests were sent. Please wait a moment and try again.",
  500: "Something went wrong on our side. Please try again in a few minutes.",
  502: "The service is temporarily unavailable. Please try again later.",
  503: "The service is temporarily unavailable. Please try again later.",
};

const SIGN_IN_STATUS_MESSAGES: Partial<Record<number, string>> = {
  401: "Wrong credentials. Please try signing in again.",
  403: "You have insufficient permissions. Contact your administrator if you need access.",
};

const GENERIC_MESSAGE_PATTERNS = [
  /^request failed/i,
  /^failed to fetch/i,
  /^network error$/i,
  /^an unexpected error occurred$/i,
  /^internal server error$/i,
  /^error$/i,
  /^unknown error$/i,
];

const TECHNICAL_MESSAGE_PATTERNS = [
  /NEXT_PUBLIC_/i,
  /\.env(\.local)?/i,
  /\bpnpm\b/i,
  /\bnpm\b/i,
  /\baxios\b/i,
  /\bfetch\b/i,
  /\bmsal\b/i,
  /\boauth\b/i,
  /\btoken\b/i,
  /\bjwt\b/i,
  /\bbearer\b/i,
  /\bid_token\b/i,
  /invalid_grant/i,
  /interaction_in_progress/i,
  /ECONNREFUSED/i,
  /ETIMEDOUT/i,
  /ENOTFOUND/i,
  /SyntaxError/i,
  /TypeError/i,
  /ReferenceError/i,
  /undefined is not/i,
  /cannot read propert/i,
  /status code \d{3}/i,
  /\b[45]\d{2}\s+(error|not found|unauthorized|forbidden)/i,
  /\/api\//i,
  /\/v1\//i,
  /\bat\s+\w+\./,
  /stack trace/i,
  /sql/i,
  /database/i,
  /null pointer/i,
];

const FRIENDLY_MESSAGE_REPLACEMENTS: [RegExp, string][] = [
  [
    /microsoft sso is not configured/i,
    "Sign-in is not set up yet. Please contact your administrator.",
  ],
  [
    /microsoft sso configuration is missing/i,
    "Sign-in is not set up yet. Please contact your administrator.",
  ],
  [
    /microsoft redirect uri is missing/i,
    "Sign-in is not set up correctly. Please contact your administrator.",
  ],
  [
    /microsoft sign-in did not complete/i,
    "We couldn't finish signing you in. Please try again.",
  ],
  [
    /microsoft sign-in did not return an id token/i,
    "Wrong credentials. Please try signing in again.",
  ],
  [
    /backend access token was not stored/i,
    "We couldn't complete your sign-in. Please try again.",
  ],
  [
    /invalid sign-in response from server/i,
    "We couldn't complete your sign-in. Please try again.",
  ],
  [
    /not a member of an allowed azure ad group|unauthorized.?azure.?ad.?group|no active groups assigned|no allowed azure/i,
    "You have no active groups assigned. Contact your administrator for access.",
  ],
  [
    /invalid email or password|authentication failed\.?$|wrong credentials/i,
    "Wrong credentials. Please check your details and try again.",
  ],
  [
    /insufficient permissions/i,
    "You have insufficient permissions to access this application.",
  ],
  [
    /user account is inactive|account is inactive/i,
    "Your account is inactive. Contact your administrator for access.",
  ],
  [
    /microsoft id token|failed to verify microsoft|token expired|invalid microsoft/i,
    "Wrong credentials. Please try signing in again.",
  ],
  [
    /maximum 5 comments allowed/i,
    "Maximum 5 observations allowed.",
  ],
  [
    /duplicate consecutive comment is not allowed/i,
    "Duplicate consecutive observation is not allowed.",
  ],
  [
    /comment not found/i,
    "This observation is no longer available.",
  ],
  [
    /failed to download file/i,
    "We couldn't download this file. Please try again.",
  ],
  [
    /alert \d+ not found/i,
    "This frame is no longer available.",
  ],
];

export class ApiError extends Error {
  readonly status: number;
  readonly code?: string;

  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
  }
}

function isGenericMessage(message: string): boolean {
  const normalized = message.trim();
  if (!normalized) return true;
  return GENERIC_MESSAGE_PATTERNS.some((pattern) => pattern.test(normalized));
}

function isTechnicalMessage(message: string): boolean {
  const normalized = message.trim();
  if (!normalized) return true;
  if (normalized.length > 240) return true;
  if (normalized.includes("\n    at ")) return true;
  if (/^Error:\s/.test(normalized)) return true;
  return TECHNICAL_MESSAGE_PATTERNS.some((pattern) => pattern.test(normalized));
}

function applyFriendlyReplacements(message: string): string | null {
  for (const [pattern, replacement] of FRIENDLY_MESSAGE_REPLACEMENTS) {
    if (pattern.test(message)) {
      return replacement;
    }
  }
  return null;
}

function isNetworkFailure(status: number, message: string): boolean {
  if (status === 0) return true;
  return /network error|failed to fetch|econnaborted|etimedout/i.test(message);
}

function statusFollowUp(status: number): string | null {
  if (status === 401) return "Please sign in again.";
  if (status === 403) {
    return "If you believe you should have access, contact your administrator.";
  }
  if (status >= 500) return "Please try again in a few minutes.";
  return STATUS_USER_MESSAGES[status] ?? null;
}

/**
 * Strip technical/API details so only end-user copy remains.
 */
export function sanitizeUserMessage(
  message: string | undefined,
  context: ErrorContext = "generic",
  status = 500,
): string {
  const trimmed = message?.trim();
  if (!trimmed) {
    return formatUserErrorMessage(undefined, status, context);
  }

  const friendly = applyFriendlyReplacements(trimmed);
  if (friendly) return friendly;

  if (
    isGenericMessage(trimmed) ||
    isTechnicalMessage(trimmed) ||
    isNetworkFailure(status, trimmed)
  ) {
    return formatUserErrorMessage(trimmed, status, context);
  }

  return trimmed;
}

/**
 * Turn a raw API/technical message into copy that is clear for end users.
 */
export function formatUserErrorMessage(
  rawMessage: string | undefined,
  status: number,
  context: ErrorContext = "generic",
): string {
  const action = CONTEXT_ACTIONS[context];
  const trimmed = rawMessage?.trim();

  if (isNetworkFailure(status, trimmed ?? "")) {
    return `We couldn't reach the server while trying to ${action}. Check your internet connection and try again.`;
  }

  const friendly = trimmed ? applyFriendlyReplacements(trimmed) : null;
  if (friendly) return friendly;

  if (trimmed && !isGenericMessage(trimmed) && !isTechnicalMessage(trimmed)) {
    const followUp = statusFollowUp(status);
    if (
      followUp &&
      !trimmed.toLowerCase().includes(followUp.toLowerCase().slice(0, 20))
    ) {
      return `${trimmed} ${followUp}`;
    }
    return trimmed;
  }

  const statusLine =
    (context === "sign-in" ? SIGN_IN_STATUS_MESSAGES[status] : undefined) ??
    STATUS_USER_MESSAGES[status] ??
    STATUS_USER_MESSAGES[500];
  return `We couldn't ${action}. ${statusLine}`;
}

export type ApiEnvelope = {
  success?: boolean;
  code?: number;
  errorMessage?: string;
  message?: string;
};

export function extractEnvelopeMessage(data: unknown): string | undefined {
  if (!data || typeof data !== "object") return undefined;
  const record = data as Record<string, unknown>;
  if (typeof record.errorMessage === "string" && record.errorMessage.trim()) {
    return record.errorMessage.trim();
  }
  if (typeof record.message === "string" && record.message.trim()) {
    return record.message.trim();
  }
  return undefined;
}

export function apiErrorFromEnvelope(
  envelope: ApiEnvelope,
  context: ErrorContext,
  fallbackStatus = 500,
): ApiError {
  const status = envelope.code ?? fallbackStatus;
  const message = formatUserErrorMessage(
    envelope.errorMessage ?? envelope.message,
    status,
    context,
  );
  return new ApiError(message, status);
}

export function getDefaultErrorTitle(context: ErrorContext = "generic"): string {
  return CONTEXT_ERROR_TITLES[context];
}

export function getUserErrorMessage(
  err: unknown,
  context: ErrorContext = "generic",
): string {
  return toApiError(err, context).message;
}

export function toApiError(
  err: unknown,
  context: ErrorContext = "generic",
): ApiError {
  if (typeof err === "string") {
    const message = sanitizeUserMessage(err, context, 500);
    return new ApiError(message, 500);
  }

  if (err instanceof ApiError) {
    const message = sanitizeUserMessage(err.message, context, err.status);
    if (message === err.message) return err;
    return new ApiError(message, err.status, err.code);
  }

  if (isAxiosError(err)) {
    const status = err.response?.status ?? 0;
    const raw = extractEnvelopeMessage(err.response?.data) ?? err.message;
    const message = sanitizeUserMessage(raw, context, status);
    return new ApiError(message, status);
  }

  if (err instanceof Error) {
    const status = isNetworkFailure(0, err.message) ? 0 : 500;
    const message = sanitizeUserMessage(err.message, context, status);
    return new ApiError(message, status);
  }

  return new ApiError(
    formatUserErrorMessage(undefined, 500, context),
    500,
  );
}
